import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '@/styles/permissionsStyles.scss';
import './ServiceProvidersPage.scss';
import {
    FaBuilding,
    FaPlus,
    FaEdit,
    FaTrash,
    FaSave,
    FaTimes,
    FaChevronDown,
    FaChevronRight,
    FaSearch,
    FaFileContract,
    FaPhone,
    FaEnvelope,
    FaGlobe,
    FaMapMarkerAlt,
    FaLink,
    FaCheckCircle,
    FaExclamationTriangle,
    FaCalendarAlt,
    FaTachometerAlt,
    FaShieldAlt,
    FaSpinner,
    FaEye,
    FaPen,
    FaBold,
    FaItalic,
    FaListUl,
    FaListOl,
    FaCode,
    FaQuoteRight,
    FaHeading,
    FaMinus,
    FaTags
} from 'react-icons/fa';
import sanitizeText from '@/utils/sanitizeText';
import * as Sentry from '@sentry/react';

/**
 * ServiceProvidersPage
 *
 * Provides a full management interface for the Service Providers / Vendor registry.
 * Includes:
 *  - List of service providers (filterable by search and category).
 *  - Add / Edit / Deactivate providers.
 *  - User-definable categories via the Category Management modal.
 *  - For each provider: view linked internet service contracts.
 *  - Internet service contracts can be viewed/edited from here and also from
 *    the Router Detail page (via the public IPs section).
 */
const ServiceProvidersPage = () => {
    const { apiFetch, user } = useAuth();

    // ── State ─────────────────────────────────────────────────────────────────────
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [categories, setCategories] = useState([]);

    // Category management modal state
    const [showCategoryManager, setShowCategoryManager] = useState(false);
    const [newCategoryInput, setNewCategoryInput] = useState('');

    // Provider form state
    const [showProviderForm, setShowProviderForm] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null);
    const [providerForm, setProviderForm] = useState(emptyProviderForm());
    const [savingProvider, setSavingProvider] = useState(false);

    // Expanded provider detail
    const [expandedProviderId, setExpandedProviderId] = useState(null);

    // ── Load data ─────────────────────────────────────────────────────────────────

    const loadProviders = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/ServiceProviders');
            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    setProviders(json.data || []);
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load service providers.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ServiceProviders/categories');
            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    setCategories(json.data || []);
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to load categories:', err);
        }
    }, [apiFetch]);

    useEffect(() => {
        loadProviders();
        loadCategories();
    }, [loadProviders, loadCategories]);

    // ── Category management ────────────────────────────────────────────────────────

    /**
     * Adds a new custom category to the list if it is not already present.
     * The category is stored by saving a temporary placeholder provider with that
     * category, or simply added to the local list to appear as a suggestion.
     * Categories are persisted via the service provider records themselves —
     * any category in use by a provider is returned by the backend.
     */
    const handleAddCategory = () => {
        const trimmed = newCategoryInput.trim();
        if (!trimmed) return;
        if (categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
            toast.info('That category already exists.');
            return;
        }
        setCategories(prev => [...prev, trimmed].sort());
        setNewCategoryInput('');
    };

    // ── Filtering ─────────────────────────────────────────────────────────────────

    const filteredProviders = providers.filter(p => {
        const matchesSearch = !searchQuery.trim()
            || p.name?.toLowerCase().includes(searchQuery.toLowerCase())
            || p.contactName?.toLowerCase().includes(searchQuery.toLowerCase())
            || p.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase())
            || p.serviceCategory?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCategory = !categoryFilter || p.serviceCategory === categoryFilter;
        const matchesActive = showInactive || p.isActive;

        return matchesSearch && matchesCategory && matchesActive;
    });

    // Derive a complete category list: API list + any from existing providers not already present
    const allCategories = [
        ...categories,
        ...providers
            .map(p => p.serviceCategory)
            .filter(cat => cat && !categories.some(c => c.toLowerCase() === cat.toLowerCase()))
    ].filter(Boolean).sort((a, b) => a.localeCompare(b));

    // ── Provider CRUD ─────────────────────────────────────────────────────────────

    const handleAddProvider = () => {
        setEditingProvider(null);
        setProviderForm(emptyProviderForm());
        setShowProviderForm(true);
    };

    const handleEditProvider = (provider) => {
        setEditingProvider(provider);
        setProviderForm({
            name: provider.name || '',
            serviceCategory: provider.serviceCategory || '',
            contactName: provider.contactName || '',
            contactPhone: provider.contactPhone || '',
            contactEmail: provider.contactEmail || '',
            supportPhone: provider.supportPhone || '',
            supportEmail: provider.supportEmail || '',
            website: provider.website || '',
            address: provider.address || '',
            mdaSupplierId: provider.mdaSupplierId ?? '',
            mdaSupplierName: provider.mdaSupplierName || '',
            notes: provider.notes || '',
            isActive: provider.isActive
        });
        setShowProviderForm(true);
    };

    const handleSaveProvider = async () => {
        if (!providerForm.name.trim()) {
            toast.error('Provider name is required.');
            return;
        }
        if (!providerForm.serviceCategory.trim()) {
            toast.error('Service category is required.');
            return;
        }

        setSavingProvider(true);
        try {
            const payload = {
                name: providerForm.name.trim(),
                serviceCategory: providerForm.serviceCategory.trim(),
                contactName: providerForm.contactName.trim() || null,
                contactPhone: providerForm.contactPhone.trim() || null,
                contactEmail: providerForm.contactEmail.trim() || null,
                supportPhone: providerForm.supportPhone.trim() || null,
                supportEmail: providerForm.supportEmail.trim() || null,
                website: providerForm.website.trim() || null,
                address: providerForm.address.trim() || null,
                mdaSupplierId: providerForm.mdaSupplierId !== '' ? parseInt(providerForm.mdaSupplierId) : null,
                notes: providerForm.notes.trim() || null,
                isActive: providerForm.isActive
            };

            const url = editingProvider
                ? `/api/ServiceProviders/${editingProvider.serviceProviderId}`
                : '/api/ServiceProviders';

            const res = await apiFetch(url, {
                method: editingProvider ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const json = await res.json();

            if (json.success) {
                toast.success(editingProvider ? 'Service provider updated.' : 'Service provider created.');
                setShowProviderForm(false);
                // Reload providers only (categories are derived from providers data)
                await loadProviders();
            } else {
                toast.error(sanitizeText(json.error || 'Failed to save provider.'));
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Error saving service provider.');
            console.error(err);
        } finally {
            setSavingProvider(false);
        }
    };

    const handleDeactivateProvider = async (provider) => {
        if (!window.confirm(`Deactivate "${provider.name}"? The provider will be hidden but historical data will be retained.`)) return;

        try {
            const res = await apiFetch(`/api/ServiceProviders/${provider.serviceProviderId}`, {
                method: 'DELETE'
            });
            const json = await res.json();
            if (json.success) {
                toast.success('Service provider deactivated.');
                await loadProviders();
            } else {
                toast.error(sanitizeText(json.error || 'Failed to deactivate.'));
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Error deactivating service provider.');
            console.error(err);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────────

    if (loading) {
        return <div className="service-providers-page"><HexagonLoader /></div>;
    }

    return (
        <div className="service-providers-page">
            {/* Page header */}
            <div className="sp-page-header">
                <div className="sp-header-left">
                    <h1><FaBuilding size={24} /> Service Providers</h1>
                    <p className="sp-header-desc">
                        Manage vendor and supplier records. Providers can be linked to service contracts, router configurations, and MDA financial accounts.
                    </p>
                </div>
                <div className="sp-header-actions">
                    <button className="sp-btn-manage-categories" onClick={() => setShowCategoryManager(true)} title="Manage service categories">
                        <FaTags size={13} /> Manage Categories
                    </button>
                    <button className="btn-create" onClick={handleAddProvider}>
                        <FaPlus size={14} /> Add Provider
                    </button>
                </div>
            </div>

            {/* Filters toolbar */}
            <div className="sp-toolbar">
                <div className="sp-search-wrap">
                    <FaSearch size={14} className="sp-search-icon" />
                    <input
                        className="sp-search"
                        type="text"
                        placeholder="Search providers..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>

                <select
                    className="sp-filter-select"
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                >
                    <option value="">All Categories</option>
                    {allCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>

                <label className="sp-inactive-toggle">
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={e => setShowInactive(e.target.checked)}
                    />
                    Show inactive
                </label>
            </div>

            {/* Providers list */}
            {filteredProviders.length === 0 ? (
                <div className="sp-empty">
                    <FaBuilding size={48} />
                    <p>No service providers found.</p>
                    <button className="btn-create" onClick={handleAddProvider}>
                        <FaPlus size={14} /> Add your first provider
                    </button>
                </div>
            ) : (
                <div className="sp-list">
                    {filteredProviders.map(provider => (
                        <ProviderCard
                            key={provider.serviceProviderId}
                            provider={provider}
                            isExpanded={expandedProviderId === provider.serviceProviderId}
                            onToggle={() => setExpandedProviderId(
                                expandedProviderId === provider.serviceProviderId ? null : provider.serviceProviderId
                            )}
                            onEdit={() => handleEditProvider(provider)}
                            onDeactivate={() => handleDeactivateProvider(provider)}
                            apiFetch={apiFetch}
                            currentUser={user}
                        />
                    ))}
                </div>
            )}

            {/* Provider form modal */}
            {showProviderForm && (
                <ProviderFormModal
                    form={providerForm}
                    onChange={setProviderForm}
                    categories={allCategories}
                    isEditing={!!editingProvider}
                    saving={savingProvider}
                    onSave={handleSaveProvider}
                    onClose={() => setShowProviderForm(false)}
                />
            )}

            {/* Category management modal */}
            {showCategoryManager && (
                <CategoryManagerModal
                    categories={allCategories}
                    newCategoryInput={newCategoryInput}
                    onNewCategoryChange={setNewCategoryInput}
                    onAddCategory={handleAddCategory}
                    onRemoveCategory={(cat) => {
                        // Only allow removing categories that are not currently in use
                        const inUse = providers.some(p => p.serviceCategory === cat);
                        if (inUse) {
                            toast.warning(`Cannot remove "${cat}" — it is used by one or more providers.`);
                            return;
                        }
                        setCategories(prev => prev.filter(c => c !== cat));
                    }}
                    providers={providers}
                    onClose={() => setShowCategoryManager(false)}
                />
            )}
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────────────
// ProviderCard — expandable row showing provider details + contracts
// ──────────────────────────────────────────────────────────────────────────────

const ProviderCard = ({ provider, isExpanded, onToggle, onEdit, onDeactivate, apiFetch, currentUser }) => {
    return (
        <div className={`sp-provider-card ${!provider.isActive ? 'sp-inactive' : ''}`}>
            {/* Card header (always visible) */}
            <div className="sp-card-header" onClick={onToggle}>
                <div className="sp-card-left">
                    <span className="sp-expand-icon">
                        {isExpanded ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
                    </span>
                    <div className="sp-card-name-block">
                        <span className="sp-provider-name">{provider.name}</span>
                        <span className={`sp-category-badge sp-cat-${provider.serviceCategory?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {provider.serviceCategory}
                        </span>
                        {!provider.isActive && (
                            <span className="sp-inactive-badge">Inactive</span>
                        )}
                    </div>
                </div>
                <div className="sp-card-meta">
                    {provider.contactName && (
                        <span className="sp-meta-item"><FaBuilding size={12} /> {provider.contactName}</span>
                    )}
                    {provider.contactPhone && (
                        <span className="sp-meta-item"><FaPhone size={12} /> {provider.contactPhone}</span>
                    )}
                    {provider.contractCount > 0 && (
                        <span className="sp-meta-item sp-contracts-badge">
                            <FaFileContract size={12} /> {provider.contractCount} contract{provider.contractCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {provider.mdaSupplierId && (
                        <span className="sp-meta-item sp-mda-badge">
                            <FaLink size={12} /> MDA #{provider.mdaSupplierId}
                        </span>
                    )}
                </div>
                <div className="sp-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="sp-btn-icon sp-btn-edit" onClick={onEdit} title="Edit provider">
                        <FaEdit size={14} />
                    </button>
                    {provider.isActive && (
                        <button className="sp-btn-icon sp-btn-delete" onClick={onDeactivate} title="Deactivate provider">
                            <FaTrash size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
                <ProviderDetail
                    provider={provider}
                    apiFetch={apiFetch}
                    currentUser={currentUser}
                />
            )}
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────────────
// ProviderDetail — expanded section showing full info + internet service contracts
// ──────────────────────────────────────────────────────────────────────────────

const ProviderDetail = ({ provider, apiFetch }) => {
    const [detailData, setDetailData] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(true);

    useEffect(() => {
        const fetchDetail = async () => {
            setLoadingDetail(true);
            try {
                const res = await apiFetch(`/api/ServiceProviders/${provider.serviceProviderId}`);
                if (res.ok) {
                    const json = await res.json();
                    if (json.success) {
                        setDetailData(json.data);
                    }
                }
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to load provider detail:', err);
            } finally {
                setLoadingDetail(false);
            }
        };

        fetchDetail();
    }, [provider.serviceProviderId, apiFetch]);

    if (loadingDetail) return <div className="sp-detail-loading">Loading…</div>;
    if (!detailData) return null;

    return (
        <div className="sp-detail">
            {/* Contact info grid */}
            <div className="sp-detail-grid">
                <div className="sp-detail-section">
                    <h3><FaBuilding size={14} /> Provider Details</h3>
                    <div className="sp-detail-fields">
                        {detailData.contactName && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label">Contact</span>
                                <span className="sp-field-value">{detailData.contactName}</span>
                            </div>
                        )}
                        {detailData.contactPhone && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaPhone size={11} /> Phone</span>
                                <span className="sp-field-value">{detailData.contactPhone}</span>
                            </div>
                        )}
                        {detailData.contactEmail && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaEnvelope size={11} /> Email</span>
                                <a className="sp-field-value sp-link" href={`mailto:${detailData.contactEmail}`}>
                                    {detailData.contactEmail}
                                </a>
                            </div>
                        )}
                        {detailData.supportPhone && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaPhone size={11} /> Support Phone</span>
                                <span className="sp-field-value">{detailData.supportPhone}</span>
                            </div>
                        )}
                        {detailData.supportEmail && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaEnvelope size={11} /> Support Email</span>
                                <a className="sp-field-value sp-link" href={`mailto:${detailData.supportEmail}`}>
                                    {detailData.supportEmail}
                                </a>
                            </div>
                        )}
                        {detailData.website && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaGlobe size={11} /> Website</span>
                                <a className="sp-field-value sp-link"
                                   href={detailData.website}
                                   target="_blank"
                                   rel="noopener noreferrer">
                                    {detailData.website}
                                </a>
                            </div>
                        )}
                        {detailData.address && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaMapMarkerAlt size={11} /> Address</span>
                                <span className="sp-field-value">{detailData.address}</span>
                            </div>
                        )}
                        {detailData.mdaSupplierId && (
                            <div className="sp-detail-field">
                                <span className="sp-field-label"><FaLink size={11} /> MDA Supplier ID</span>
                                <span className="sp-field-value">{detailData.mdaSupplierId}</span>
                            </div>
                        )}
                        {detailData.notes && (
                            <div className="sp-detail-field sp-field-full">
                                <span className="sp-field-label">Notes</span>
                                <div className="sp-field-value sp-notes sp-notes-markdown">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {detailData.notes}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Internet Service Contracts */}
                {detailData.internetServiceContracts && detailData.internetServiceContracts.length > 0 && (
                    <div className="sp-detail-section sp-contracts-section">
                        <h3><FaFileContract size={14} /> Internet Service Contracts ({detailData.internetServiceContracts.length})</h3>
                        <div className="sp-contracts-list">
                            {detailData.internetServiceContracts.map(contract => (
                                <ContractSummaryCard key={contract.internetServiceContractId} contract={contract} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────────────
// ContractSummaryCard — shows a compact summary of an ISP contract
// ──────────────────────────────────────────────────────────────────────────────

const ContractSummaryCard = ({ contract }) => {
    const expiryDate = contract.contractExpiryDate ? new Date(contract.contractExpiryDate) : null;
    const daysUntilExpiry = expiryDate
        ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    const getExpiryClass = () => {
        if (daysUntilExpiry === null) return '';
        if (daysUntilExpiry <= 7) return 'expiry-critical';
        if (daysUntilExpiry <= 30) return 'expiry-warning';
        return 'expiry-ok';
    };

    return (
        <div className="sp-contract-card">
            <div className="sp-contract-header">
                <span className="sp-contract-label">
                    <strong>{contract.routerPublicAddressLabel || contract.routerPublicAddressIp}</strong>
                    {contract.routerName && (
                        <span className="sp-contract-router"> — {contract.routerName}</span>
                    )}
                </span>
                {contract.routerPublicAddressIp && (
                    <span className="sp-contract-ip">{contract.routerPublicAddressIp}</span>
                )}
            </div>
            <div className="sp-contract-details">
                {(contract.expectedDownloadMbps || contract.expectedUploadMbps) && (
                    <span className="sp-contract-detail">
                        <FaTachometerAlt size={11} />
                        {contract.expectedDownloadMbps ? `↓${contract.expectedDownloadMbps} Mbps` : ''}
                        {contract.expectedDownloadMbps && contract.expectedUploadMbps ? ' / ' : ''}
                        {contract.expectedUploadMbps ? `↑${contract.expectedUploadMbps} Mbps` : ''}
                    </span>
                )}
                {contract.uptimeSlaPercent && (
                    <span className="sp-contract-detail">
                        <FaShieldAlt size={11} /> {contract.uptimeSlaPercent}% SLA
                    </span>
                )}
                {expiryDate && (
                    <span className={`sp-contract-detail sp-expiry ${getExpiryClass()}`}>
                        <FaCalendarAlt size={11} />
                        {daysUntilExpiry !== null && daysUntilExpiry < 0
                            ? <><FaExclamationTriangle size={11} /> Expired</>
                            : daysUntilExpiry !== null
                                ? `Expires in ${daysUntilExpiry}d (${expiryDate.toLocaleDateString()})`
                                : expiryDate.toLocaleDateString()
                        }
                    </span>
                )}
            </div>
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────────────
// ProviderFormModal — add / edit provider form
// Features:
//   - Free-text input with datalist for Service Category (user-definable)
//   - MDA Supplier searchable dropdown (async search against MDA DB)
//   - Markdown Notes editor with Edit/Preview toggle
// ──────────────────────────────────────────────────────────────────────────────

const ProviderFormModal = ({ form, onChange, categories, isEditing, saving, onSave, onClose }) => {
    const { apiFetch } = useAuth();
    const updateField = (fieldName, value) => {
        onChange(prev => ({ ...prev, [fieldName]: value }));
    };

    // MDA Supplier search state
    const [mdaSearchTerm, setMdaSearchTerm] = useState(
        form.mdaSupplierName || (form.mdaSupplierId ? `MDA #${form.mdaSupplierId}` : '')
    );
    const [mdaResults, setMdaResults] = useState([]);
    const [mdaSearching, setMdaSearching] = useState(false);
    const [showMdaDropdown, setShowMdaDropdown] = useState(false);
    const mdaSearchTimeout = useRef(null);
    const mdaContainerRef = useRef(null);

    // Notes editor mode: 'edit' | 'preview'
    const [notesMode, setNotesMode] = useState('edit');

    // Sync the MDA search display text when the parent form prop changes
    // (e.g. when switching between editing different providers)
    useEffect(() => {
        setMdaSearchTerm(
            form.mdaSupplierName || (form.mdaSupplierId ? `MDA #${form.mdaSupplierId}` : '')
        );
    }, [form.mdaSupplierId, form.mdaSupplierName]);

    // Clear the debounce timer on unmount to prevent memory leaks
    useEffect(() => {
        return () => { clearTimeout(mdaSearchTimeout.current); };
    }, []);

    // Search MDA suppliers when the user types in the MDA field
    const handleMdaSearchChange = (value) => {
        setMdaSearchTerm(value);

        // Clear current selection if the user changes the text
        if (form.mdaSupplierId) {
            updateField('mdaSupplierId', '');
            updateField('mdaSupplierName', '');
        }

        clearTimeout(mdaSearchTimeout.current);

        if (value.trim().length < 2) {
            setMdaResults([]);
            setShowMdaDropdown(false);
            return;
        }

        mdaSearchTimeout.current = setTimeout(async () => {
            setMdaSearching(true);
            try {
                const res = await apiFetch(`/api/ServiceProviders/mda-suppliers?search=${encodeURIComponent(value.trim())}`);
                const json = await res.json();
                if (json.success) {
                    setMdaResults(json.data || []);
                    setShowMdaDropdown((json.data || []).length > 0);
                }
            } catch (err) {
                Sentry.captureException(err);
                console.error('MDA supplier search failed:', err);
            } finally {
                setMdaSearching(false);
            }
        }, 350);
    };

    // Select a supplier from the dropdown
    const handleMdaSelect = (supplier) => {
        updateField('mdaSupplierId', supplier.supplierID);
        updateField('mdaSupplierName', supplier.supplierName);
        setMdaSearchTerm(`${supplier.supplierName} (${supplier.supplierCode})`);
        setShowMdaDropdown(false);
        setMdaResults([]);
    };

    // Clear MDA selection
    const handleMdaClear = () => {
        updateField('mdaSupplierId', '');
        updateField('mdaSupplierName', '');
        setMdaSearchTerm('');
        setMdaResults([]);
        setShowMdaDropdown(false);
    };

    // Close MDA dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (mdaContainerRef.current && !mdaContainerRef.current.contains(e.target)) {
                setShowMdaDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Markdown toolbar helpers
    const notesRef = useRef(null);
    const applyMarkdown = (before, after, placeholder) => {
        const el = notesRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const selected = form.notes.substring(start, end) || placeholder;
        const newText = form.notes.substring(0, start) + before + selected + after + form.notes.substring(end);
        updateField('notes', newText);
        setTimeout(() => {
            el.focus();
            const pos = start + before.length + selected.length + after.length;
            el.setSelectionRange(pos, pos);
        }, 0);
    };

    const notesToolbar = [
        { title: 'Heading', icon: <FaHeading />, action: () => applyMarkdown('## ', '', 'Heading') },
        { title: 'Bold', icon: <FaBold />, action: () => applyMarkdown('**', '**', 'bold text') },
        { title: 'Italic', icon: <FaItalic />, action: () => applyMarkdown('_', '_', 'italic text') },
        { title: 'Code', icon: <FaCode />, action: () => applyMarkdown('`', '`', 'code') },
        { sep: true },
        { title: 'Bullet list', icon: <FaListUl />, action: () => applyMarkdown('\n- ', '', 'item') },
        { title: 'Numbered list', icon: <FaListOl />, action: () => applyMarkdown('\n1. ', '', 'item') },
        { title: 'Quote', icon: <FaQuoteRight />, action: () => applyMarkdown('> ', '', 'quote') },
        { title: 'Rule', icon: <FaMinus />, action: () => applyMarkdown('\n---\n', '', '') },
    ];

    return (
        <div className="sp-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sp-modal">
                <div className="sp-modal-header">
                    <h2>{isEditing ? 'Edit Service Provider' : 'Add Service Provider'}</h2>
                    <button className="sp-modal-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="sp-modal-body">
                    {/* Basic Info */}
                    <div className="sp-form-section">
                        <h3>Basic Information</h3>
                        <div className="sp-form-grid">
                            <div className="sp-form-group sp-full">
                                <label>Provider Name *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => updateField('name', e.target.value)}
                                    placeholder="e.g. CityFibre Networks"
                                />
                            </div>
                            <div className="sp-form-group">
                                {/* 
                                  * Service Category uses a free-text input with a <datalist>
                                  * so users can select predefined options OR type a custom category.
                                  * New categories become available once a provider is saved.
                                  */}
                                <label>Service Category *</label>
                                <input
                                    type="text"
                                    list="sp-categories-list"
                                    value={form.serviceCategory}
                                    onChange={e => updateField('serviceCategory', e.target.value)}
                                    placeholder="Type or select a category…"
                                    autoComplete="off"
                                />
                                <datalist id="sp-categories-list">
                                    {categories.map(cat => (
                                        <option key={cat} value={cat} />
                                    ))}
                                </datalist>
                                <small className="sp-field-hint">
                                    Select a suggested category or type a custom one. New categories are saved automatically.
                                </small>
                            </div>
                            <div className="sp-form-group" ref={mdaContainerRef} style={{ position: 'relative' }}>
                                {/*
                                  * MDA Supplier ID — searchable lookup against the MDA database.
                                  * The user types a supplier name and selects from the dropdown.
                                  * The numeric SupplierID is stored; the display name is shown.
                                  */}
                                <label>MDA Supplier</label>
                                <div className="sp-mda-search-wrap">
                                    <input
                                        type="text"
                                        value={mdaSearchTerm}
                                        onChange={e => handleMdaSearchChange(e.target.value)}
                                        placeholder="Search MDA supplier by name…"
                                        autoComplete="off"
                                    />
                                    {mdaSearching && (
                                        <span className="sp-mda-spinner"><FaSpinner className="spin" /></span>
                                    )}
                                    {form.mdaSupplierId && (
                                        <button
                                            type="button"
                                            className="sp-mda-clear"
                                            onClick={handleMdaClear}
                                            title="Clear selection"
                                        >
                                            <FaTimes size={11} />
                                        </button>
                                    )}
                                </div>
                                {form.mdaSupplierId && (
                                    <small className="sp-field-hint sp-mda-selected">
                                        <FaCheckCircle size={11} style={{ color: '#28a745' }} /> MDA Supplier ID: {form.mdaSupplierId}
                                    </small>
                                )}
                                {!form.mdaSupplierId && mdaSearchTerm.length < 2 && (
                                    <small className="sp-field-hint">
                                        Type at least 2 characters to search the MDA database.
                                    </small>
                                )}
                                {showMdaDropdown && (
                                    <div className="sp-mda-dropdown">
                                        {mdaResults.map(supplier => (
                                            <div
                                                key={supplier.supplierID}
                                                className="sp-mda-option"
                                                onClick={() => handleMdaSelect(supplier)}
                                            >
                                                <span className="sp-mda-name">{supplier.supplierName}</span>
                                                <span className="sp-mda-code">{supplier.supplierCode}</span>
                                                {supplier.contactName && (
                                                    <span className="sp-mda-contact">{supplier.contactName}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {mdaSearchTerm.length >= 2 && !mdaSearching && mdaResults.length === 0 && !form.mdaSupplierId && (
                                    <div className="sp-mda-dropdown sp-mda-empty">
                                        No MDA suppliers found for &ldquo;{mdaSearchTerm}&rdquo;.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Primary Contact */}
                    <div className="sp-form-section">
                        <h3>Primary Contact</h3>
                        <div className="sp-form-grid">
                            <div className="sp-form-group">
                                <label>Contact Name</label>
                                <input
                                    type="text"
                                    value={form.contactName}
                                    onChange={e => updateField('contactName', e.target.value)}
                                    placeholder="Full name"
                                />
                            </div>
                            <div className="sp-form-group">
                                <label>Contact Phone</label>
                                <input
                                    type="tel"
                                    value={form.contactPhone}
                                    onChange={e => updateField('contactPhone', e.target.value)}
                                    placeholder="+27 XX XXX XXXX"
                                />
                            </div>
                            <div className="sp-form-group">
                                <label>Contact Email</label>
                                <input
                                    type="email"
                                    value={form.contactEmail}
                                    onChange={e => updateField('contactEmail', e.target.value)}
                                    placeholder="contact@example.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Support Contact */}
                    <div className="sp-form-section">
                        <h3>Support / Help-desk</h3>
                        <div className="sp-form-grid">
                            <div className="sp-form-group">
                                <label>Support Phone</label>
                                <input
                                    type="tel"
                                    value={form.supportPhone}
                                    onChange={e => updateField('supportPhone', e.target.value)}
                                    placeholder="NOC / helpdesk number"
                                />
                            </div>
                            <div className="sp-form-group">
                                <label>Support Email</label>
                                <input
                                    type="email"
                                    value={form.supportEmail}
                                    onChange={e => updateField('supportEmail', e.target.value)}
                                    placeholder="support@example.com"
                                />
                            </div>
                            <div className="sp-form-group">
                                <label>Website</label>
                                <input
                                    type="url"
                                    value={form.website}
                                    onChange={e => updateField('website', e.target.value)}
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Address & Notes */}
                    <div className="sp-form-section">
                        <h3>Address &amp; Notes</h3>
                        <div className="sp-form-grid">
                            <div className="sp-form-group sp-full">
                                <label>Address</label>
                                <input
                                    type="text"
                                    value={form.address}
                                    onChange={e => updateField('address', e.target.value)}
                                    placeholder="Physical / postal address"
                                />
                            </div>

                            {/* Markdown Notes editor */}
                            <div className="sp-form-group sp-full">
                                <div className="sp-notes-editor-header">
                                    <label>Notes</label>
                                    <div className="sp-notes-mode-tabs">
                                        <button
                                            type="button"
                                            className={`sp-notes-mode-btn ${notesMode === 'edit' ? 'active' : ''}`}
                                            onClick={() => setNotesMode('edit')}
                                        >
                                            <FaPen size={11} /> Edit
                                        </button>
                                        <button
                                            type="button"
                                            className={`sp-notes-mode-btn ${notesMode === 'preview' ? 'active' : ''}`}
                                            onClick={() => setNotesMode('preview')}
                                        >
                                            <FaEye size={11} /> Preview
                                        </button>
                                    </div>
                                </div>

                                {notesMode === 'edit' && (
                                    <>
                                        {/* Markdown toolbar */}
                                        <div className="sp-notes-toolbar">
                                            {notesToolbar.map((btn, i) =>
                                                btn.sep
                                                    ? <span key={i} className="sp-notes-toolbar-sep" />
                                                    : (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            title={btn.title}
                                                            className="sp-notes-toolbar-btn"
                                                            onClick={btn.action}
                                                        >
                                                            {btn.icon}
                                                        </button>
                                                    )
                                            )}
                                        </div>
                                        <textarea
                                            ref={notesRef}
                                            value={form.notes}
                                            onChange={e => updateField('notes', e.target.value)}
                                            placeholder="Account number, SLA notes, renewal terms, etc. Supports **Markdown**."
                                            rows={6}
                                            className="sp-notes-textarea"
                                        />
                                    </>
                                )}

                                {notesMode === 'preview' && (
                                    <div className="sp-notes-preview sp-notes-markdown">
                                        {form.notes.trim()
                                            ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.notes}</ReactMarkdown>
                                            : <em style={{ color: '#aaa' }}>Nothing to preview yet.</em>
                                        }
                                    </div>
                                )}
                            </div>

                            {isEditing && (
                                <div className="sp-form-group">
                                    <label>Status</label>
                                    <select
                                        value={form.isActive ? 'active' : 'inactive'}
                                        onChange={e => updateField('isActive', e.target.value === 'active')}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="sp-modal-footer">
                    <button className="sp-btn-cancel" onClick={onClose} disabled={saving}>
                        <FaTimes size={13} /> Cancel
                    </button>
                    <button className="sp-btn-save" onClick={onSave} disabled={saving}>
                        {saving ? 'Saving…' : <><FaSave size={13} /> Save Provider</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────────────
// CategoryManagerModal — view, add and remove service categories
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Provides a simple UI for managing the list of available service categories.
 * Categories that are in use by existing providers cannot be deleted.
 */
const CategoryManagerModal = ({ categories, newCategoryInput, onNewCategoryChange, onAddCategory, onRemoveCategory, providers, onClose }) => {
    const inUseCounts = {};
    for (const p of providers) {
        if (p.serviceCategory) {
            inUseCounts[p.serviceCategory] = (inUseCounts[p.serviceCategory] || 0) + 1;
        }
    }

    return (
        <div className="sp-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sp-modal sp-modal-sm">
                <div className="sp-modal-header">
                    <h2><FaTags size={16} /> Manage Categories</h2>
                    <button className="sp-modal-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="sp-modal-body">
                    <p className="sp-category-manager-desc">
                        Define the categories available when adding or editing service providers.
                        Categories in use by existing providers cannot be removed.
                    </p>

                    {/* Add new category */}
                    <div className="sp-category-add-row">
                        <input
                            type="text"
                            value={newCategoryInput}
                            onChange={e => onNewCategoryChange(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && onAddCategory()}
                            placeholder="New category name…"
                            className="sp-category-input"
                        />
                        <button
                            type="button"
                            className="sp-btn-add-category"
                            onClick={onAddCategory}
                            disabled={!newCategoryInput.trim()}
                        >
                            <FaPlus size={12} /> Add
                        </button>
                    </div>

                    {/* Category list */}
                    <div className="sp-category-list">
                        {categories.length === 0 ? (
                            <p style={{ color: '#aaa', fontStyle: 'italic', padding: '12px 0' }}>
                                No categories defined yet.
                            </p>
                        ) : (
                            categories.map(cat => {
                                const count = inUseCounts[cat] || 0;
                                return (
                                    <div key={cat} className="sp-category-row">
                                        <span className="sp-category-name">{cat}</span>
                                        {count > 0 && (
                                            <span className="sp-category-in-use">
                                                {count} provider{count !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            className="sp-btn-icon sp-btn-delete"
                                            onClick={() => onRemoveCategory(cat)}
                                            title={count > 0 ? 'In use — cannot remove' : 'Remove category'}
                                            disabled={count > 0}
                                        >
                                            <FaTrash size={12} />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="sp-modal-footer">
                    <button className="sp-btn-cancel" onClick={onClose}>
                        <FaTimes size={13} /> Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────────────
// Helper: empty form state
// ──────────────────────────────────────────────────────────────────────────────

function emptyProviderForm() {
    return {
        name: '',
        serviceCategory: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        supportPhone: '',
        supportEmail: '',
        website: '',
        address: '',
        mdaSupplierId: '',
        mdaSupplierName: '',
        notes: '',
        isActive: true
    };
}

export default ServiceProvidersPage;
